// Mapbox Configuration and Utilities
import { CUSTOM_NAVIGATION_STYLE } from './mapbox-config';

// Consistent fallback token across the application (fallback for development/demo)
// Using your actual Mapbox token for better functionality
const FALLBACK_MAPBOX_TOKEN = 'pk.eyJ1IjoiZnJpZmViIiwiYSI6ImNqOW1ndDJ3eDBzanMzM3F6ZTZiYWk3aTUifQ.OQ3YHA1tVZIREuS37uOn4g';

export const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || FALLBACK_MAPBOX_TOKEN;

// Enhanced environment validation with better messaging
export const validateMapboxEnvironment = () => {
  const token = import.meta.env.VITE_MAPBOX_TOKEN;
  const hasCustomToken = token &&
                        token !== 'your_mapbox_access_token' &&
                        token !== 'your_mapbox_public_token' &&
                        token.startsWith('pk.');  // Valid Mapbox tokens start with 'pk.'

  if (!hasCustomToken) {
    // Only warn if we're truly using the fallback, not if a valid token is configured
    if (MAPBOX_ACCESS_TOKEN === FALLBACK_MAPBOX_TOKEN && !import.meta.env.VITE_MAPBOX_TOKEN) {
      const message = import.meta.env.DEV
        ? '⚠️ Using fallback Mapbox token for development. Set VITE_MAPBOX_TOKEN for production.'
        : '⚠️ Mapbox access token not configured. Maps may have limited functionality.';

      console.warn(message);
      return false;
    }
  }

  if (import.meta.env.DEV && hasCustomToken) {
    console.log('✅ Custom Mapbox access token configured');
  }

  return true;
};

// Validate environment on import
validateMapboxEnvironment();

export interface MapboxConfig {
  accessToken: string;
  style: string;
  center: [number, number];
  zoom: number;
}

export interface MapMarker {
  id: string;
  longitude: number;
  latitude: number;
  title: string;
  description?: string;
  type: 'display' | 'sequence' | 'user';
  rating?: number;
  image_url?: string;
  holiday_type?: string;
  address?: string;
}

export interface MapBounds {
  sw: [number, number]; // [longitude, latitude]
  ne: [number, number]; // [longitude, latitude]
}

export class MapboxService {
  private config: MapboxConfig;

  constructor(config?: Partial<MapboxConfig>) {
    this.config = {
      accessToken: MAPBOX_ACCESS_TOKEN,
      style: CUSTOM_NAVIGATION_STYLE, // Use custom navigation style as default
      center: [-98.5795, 39.8283], // Center of USA
      zoom: 4,
      ...config
    };
  }

  getConfig(): MapboxConfig {
    return this.config;
  }

  updateStyle(style: 'navigation' | 'streets' | 'satellite' | 'terrain' | 'dark' | 'light'): void {
    const styleMap = {
      navigation: CUSTOM_NAVIGATION_STYLE, // Custom navigation style (preferred)
      streets: 'mapbox://styles/mapbox/streets-v12',
      satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
      terrain: 'mapbox://styles/mapbox/outdoors-v12',
      dark: 'mapbox://styles/mapbox/dark-v11',
      light: 'mapbox://styles/mapbox/light-v11'
    };

    this.config.style = styleMap[style];
  }

  // Calculate bounds for a list of markers
  calculateBounds(markers: MapMarker[], padding: number = 0.01): MapBounds | null {
    if (markers.length === 0) return null;

    let minLng = markers[0].longitude;
    let maxLng = markers[0].longitude;
    let minLat = markers[0].latitude;
    let maxLat = markers[0].latitude;

    markers.forEach(marker => {
      minLng = Math.min(minLng, marker.longitude);
      maxLng = Math.max(maxLng, marker.longitude);
      minLat = Math.min(minLat, marker.latitude);
      maxLat = Math.max(maxLat, marker.latitude);
    });

    return {
      sw: [minLng - padding, minLat - padding],
      ne: [maxLng + padding, maxLat + padding]
    };
  }

  // Create a circle around a point (for radius visualization)
  createCircleGeoJSON(center: [number, number], radiusInMiles: number) {
    const points = 64;
    const coords = [];
    const distanceX = radiusInMiles / (111.32 * Math.cos(center[1] * Math.PI / 180));
    const distanceY = radiusInMiles / 110.54;

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI);
      const x = distanceX * Math.cos(theta);
      const y = distanceY * Math.sin(theta);
      coords.push([center[0] + x, center[1] + y]);
    }
    coords.push(coords[0]); // Close the circle

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords]
      },
      properties: {
        radius: radiusInMiles
      }
    };
  }

  // Create GeoJSON from markers
  createMarkersGeoJSON(markers: MapMarker[]) {
    return {
      type: 'FeatureCollection',
      features: markers.map(marker => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.longitude, marker.latitude]
        },
        properties: {
          id: marker.id,
          title: marker.title,
          description: marker.description,
          type: marker.type,
          rating: marker.rating,
          image_url: marker.image_url,
          holiday_type: marker.holiday_type,
          address: marker.address
        }
      }))
    };
  }

  // Generate cluster configuration for markers
  getClusterConfig() {
    return {
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      clusterProperties: {
        // Count displays vs sequences
        displays: ['+', ['case', ['==', ['get', 'type'], 'display'], 1, 0]],
        sequences: ['+', ['case', ['==', ['get', 'type'], 'sequence'], 1, 0]],
        // Average rating
        avg_rating: ['/', ['+', ['get', 'rating']], ['get', 'point_count']]
      }
    };
  }

  // Style configuration for different marker types
  getMarkerStyles() {
    return {
      display: {
        christmas: { color: '#dc2626', size: 8 }, // red-600
        halloween: { color: '#ea580c', size: 8 }, // orange-600
        yearRound: { color: '#2563eb', size: 8 }, // blue-600
        default: { color: '#059669', size: 8 } // emerald-600
      },
      sequence: {
        color: '#7c3aed', // violet-600
        size: 6
      },
      user: {
        color: '#1f2937', // gray-800
        size: 10
      }
    };
  }
}

export default MapboxService; 